import { useEffect, useMemo, useState } from 'react';
import { PhoneField, SelectField, TextArea, TextField } from '../components/FormField';
import RelationField from '../components/RelationField';
import Section from '../components/Section';
import { scrollToFirstError } from '../utils/scrollToFirstError';
import {
  COUNTRY_OPTIONS,
  STATE_OPTIONS_BY_COUNTRY,
  countryHasStateList,
  countryRequiresState,
} from '../utils/addressOptions';
import {
  EMPLOYMENT_STATUSES,
  EMPLOYMENT_TYPES,
  PORTAL_ROLES,
} from '../services/team';
import { listSuppliers } from '../services/suppliers';
import { liaisonManagersAtPlant } from '../utils/supplierScope';
import { APPAREL_SIZES, SHIRT_FITS, VEST_COLORS } from '../services/requests';

// Employment types that come with an external business entity (LLC, etc.).
// Business Entity only renders for these — W-2 employees don't need it.
const ENTITY_REQUIRED_TYPES = new Set(['Contractor', 'Sub-Contractor']);

// Every Proxemics team email is @proxemicsconsulting.com. We render the
// domain next to the input as a locked suffix so the director only types the
// local part. Stored value on the record is still the full address.
const PROXEMICS_EMAIL_DOMAIN = '@proxemicsconsulting.com';

// Org hierarchy: Director > Regional Manager > Liaison Manager > Liaison.
// A Regional Manager doesn't report up to another RM, and a Liaison Manager
// doesn't report up to another LM — so we hide those fields for people
// already at that level. Directors and Executives sit above both and have
// neither (Executive = staff above plant-level work).
const HIDE_RM_WHEN = new Set(['Director', 'Executive', 'RegionalManager']);
const HIDE_LM_WHEN = new Set(['Director', 'Executive', 'RegionalManager', 'LiaisonManager']);

// Executives are Proxemics staff, not assigned to any plant — so the whole
// Plant Assignment section (managers + plant checklist) is hidden for them.
const HIDE_PLANTS_WHEN = new Set(['Executive']);

// Roles whose manager can be a Director OR a Regional Manager (Oct 2026).
// Executives (staff) show the field outside Plant Assignment, optional.
const DIRECTOR_MANAGER_ROLES = new Set(['LiaisonManager', 'Executive']);

// Friendly labels for the error summary banner. The validate() function keys
// errors by the raw model field name (e.g. "MailingZip"), but the banner is
// read by the director, so we translate those into the same wording she sees
// next to each input. Kept in sync with the field labels used below.
const FIELD_LABELS = {
  FirstName: 'First name',
  LastName: 'Last name',
  Role: 'Job title / role',
  Email: 'Proxemics email',
  Phone: 'Phone',
  QRImage: 'QR image',
  EmploymentType: 'Employment type',
  EmploymentStatus: 'Employment status',
  BusinessEntity: 'Business entity',
  StartDate: 'Start date',
  EndDate: 'End date',
  RegionalManager: 'Regional Manager',
  LiaisonManagers: 'Liaison Managers',
  Plants: 'Plant assignments',
  MailingStreet: 'Street',
  MailingCity: 'City',
  MailingState: 'State / Province',
  MailingZip: 'Zip / Postal Code',
  MailingCountry: 'Country',
  PersonalEmail: 'Personal email',
  PortalRole: 'Portal role',
};

/**
 * Team-member create / edit form. Sectioned because there's a lot — one
 * column so the sensitive fields don't sit awkwardly beside the basic ones.
 *
 * Plants: a member can be assigned to one or more plants. The checklist
 * below covers the pilot-era plant counts cleanly; once we have dozens of
 * plants we'll switch to a typeahead. Per-plant OEM/Region/Country/State
 * stay on the Plant record — the detail page reads them from there, so
 * nothing is denormalized onto the team member's row.
 *
 * Props:
 *   - initial    — an existing team-member record for edit, or {} for create
 *   - plants     — array of plant records (decoded via services/plants.js)
 *   - team       — full team roster (decoded via services/team.js); used to
 *                  build the Regional Manager + Liaison Manager pickers so the
 *                  director selects existing team members instead of typing
 *                  names. Only Active / On Leave members with the matching
 *                  PortalRole appear as options. Safe to leave empty — the
 *                  picker will just render a "no eligible members" hint.
 *   - saving     — boolean disables the submit button
 *   - onSave, onCancel
 */
// Roles whose Team record carries a per-plant Supplier Assignment
// checklist. Liaisons: which suppliers they support. Liaison Managers:
// which suppliers they're responsible for billing when a plant has more
// than one LM (Sep 2026).
const SUPPLIER_ASSIGNMENT_ROLES = new Set(['Liaison', 'LiaisonManager']);

export default function TeamForm({
  initial,
  plants = [],
  team = [],
  saving = false,
  onSave,
  onCancel,
}) {
  const [model, setModel] = useState(() => buildModel(initial));
  const [errors, setErrors] = useState({});
  // Tracks whether the director has hit "Add Team Member" / "Save Changes"
  useEffect(() => {
    setModel(buildModel(initial));
    setErrors({});
  }, [initial?.id]);

  // Suppliers — needed for the Liaison-only "Supplier Assignment" picker
  // (which suppliers this liaison supports at each assigned plant).
  // Loaded here so parent pages don't all have to pass suppliers down.
  // Non-fatal: if the fetch fails, the picker just shows "no suppliers".
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listSuppliers();
        if (!cancelled) setSuppliers(rows || []);
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Eligible managers are pulled from the current team roster so the director
  // picks a real person instead of typing a name (which drifts out of sync
  // with record IDs). We keep the stored value as the full display name — the
  // rest of the app matches managers by normalized name, and that avoids a
  // schema migration for the column.
  //
  // Filters:
  //   - PortalRole must match ('RegionalManager' or 'LiaisonManager')
  //   - EmploymentStatus must be Active or On Leave (Exited can't manage)
  //   - Can't pick yourself as your own manager — exclude the editing record
  const managerEligible = (m) =>
    m && m.EmploymentStatus !== 'Exited' && m.id !== initial?.id;
  // Liaison Managers and Executives (non-plant staff) can report to a
  // Director instead of a Regional Manager (per Jillian, Oct 2026), so
  // Directors join the list for those roles. Liaisons stay RM-only.
  const directorAllowed = DIRECTOR_MANAGER_ROLES.has(model.PortalRole);
  const rmOptions = useMemo(() => {
    return (team || [])
      .filter(
        (m) =>
          (m.PortalRole === 'RegionalManager' ||
            (directorAllowed && m.PortalRole === 'Director')) &&
          managerEligible(m),
      )
      .map((m) => fullName(m))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, initial?.id, directorAllowed]);
  const directorNames = useMemo(
    () =>
      new Set(
        (team || [])
          .filter((m) => m.PortalRole === 'Director')
          .map((m) => fullName(m))
          .filter(Boolean),
      ),
    [team],
  );
  const lmOptions = useMemo(() => {
    return (team || [])
      .filter((m) => m.PortalRole === 'LiaisonManager' && managerEligible(m))
      .map((m) => fullName(m))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, initial?.id]);

  const set = (k, v) => setModel((m) => ({ ...m, [k]: v }));

  // Country / State dropdown options for the mailing address. Keep any
  // non-standard saved value as an option so editing a legacy record doesn't
  // blank a free-typed country/state.
  const countryOptions = useMemo(() => {
    const opts = [...COUNTRY_OPTIONS];
    if (model.MailingCountry && !opts.includes(model.MailingCountry)) {
      opts.unshift(model.MailingCountry);
    }
    return opts;
  }, [model.MailingCountry]);
  const stateOptions = useMemo(() => {
    const base = STATE_OPTIONS_BY_COUNTRY[model.MailingCountry] || [];
    const opts = [...base];
    if (model.MailingState && !opts.includes(model.MailingState)) {
      opts.unshift(model.MailingState);
    }
    return opts;
  }, [model.MailingCountry, model.MailingState]);

  const togglePlant = (code, checked) => {
    setModel((m) => {
      const cur = Array.isArray(m.plantCodes) ? m.plantCodes : [];
      if (checked) {
        if (cur.includes(code)) return m;
        return { ...m, plantCodes: [...cur, code] };
      }
      return { ...m, plantCodes: cur.filter((c) => c !== code) };
    });
  };

  // --- Supplier Assignment (Liaisons only) --------------------------------
  // Which supplier(s) this liaison supports at each assigned plant — many
  // liaisons support more than one, so it's a checklist per plant. Stored
  // as { PLANTCODE: [supplierCodes] } on the record (per Jillian, July
  // 2026).
  const toggleSupplierAssignment = (plantCode, supplierCode, checked) => {
    setModel((m) => {
      const cur =
        m.supplierAssignments && typeof m.supplierAssignments === 'object'
          ? m.supplierAssignments
          : {};
      const list = Array.isArray(cur[plantCode]) ? cur[plantCode] : [];
      const next = checked
        ? list.includes(supplierCode)
          ? list
          : [...list, supplierCode]
        : list.filter((c) => c !== supplierCode);
      return { ...m, supplierAssignments: { ...cur, [plantCode]: next } };
    });
  };
  // Suppliers with a plant assignment at the given plant, alphabetical.
  const suppliersForPlant = (plantCode) => {
    const target = String(plantCode || '').toUpperCase().trim();
    return (suppliers || [])
      .filter((s) =>
        (s.plantAssignments || []).some(
          (pa) => String(pa.plantCode || '').toUpperCase().trim() === target,
        ),
      )
      .sort((a, b) => (a.Title || '').localeCompare(b.Title || ''));
  };

  // --- Liaison Managers (checklist of eligible team members) ---------------
  // A person can report up to one or more Liaison Managers. We store as an
  // array of display names so the existing name-based matching (org chart,
  // Plant detail "LMs at this plant") keeps working. The checklist ensures
  // every entry is a real team member — no free-text typos.
  const toggleLm = (name, checked) => {
    setModel((m) => {
      const cur = Array.isArray(m.liaisonManagers) ? m.liaisonManagers : [];
      if (checked) {
        if (cur.includes(name)) return m;
        return { ...m, liaisonManagers: [...cur, name] };
      }
      return { ...m, liaisonManagers: cur.filter((n) => n !== name) };
    });
  };

  // --- QR image upload ---------------------------------------------------
  // Resize to 400x400 PNG on the client so a huge phone-camera upload
  // doesn't blow past SharePoint's Note-column limit (~63,999 chars). After
  // encoding, the data URL is stored on the record as `QRImage` and rendered
  // directly on the detail page — no blob storage needed for a pilot.
  const [qrError, setQrError] = useState('');
  const onQrFile = async (file) => {
    setQrError('');
    if (!file) return;
    try {
      const dataUrl = await fileToResizedDataUrl(file, 400);
      if (dataUrl.length > 60_000) {
        setQrError('That image is too large even after resizing — try a simpler QR image.');
        return;
      }
      set('QRImage', dataUrl);
    } catch (err) {
      console.error('[TeamForm] QR upload failed', err);
      setQrError('Could not read that file. Please try a PNG or JPG.');
    }
  };

  // Everything in the form is required except Birthdate, Emergency Contact,
  // and Notes. Business Entity only applies when the designation is a
  // contractor type; End Date only applies when the member has Exited.
  // Mailing address is split into five columns — all required so addresses
  // round-trip cleanly (no free-text ambiguity).
  const validate = () => {
    const next = {};
    const req = (k, msg = 'Required') => {
      if (!model[k] || (typeof model[k] === 'string' && !model[k].trim())) {
        next[k] = msg;
      }
    };
    req('FirstName');
    req('LastName');
    req('Role');
    // Empty local part ("@proxemicsconsulting.com") counts as missing.
    if (!emailLocalPart(model.Email).trim()) next.Email = 'Required';
    req('Phone');
    // QR image is optional (June 2026) — the detail page simply skips
    // the QR block when no image is on file.
    req('EmploymentType');
    req('EmploymentStatus');
    if (ENTITY_REQUIRED_TYPES.has(model.EmploymentType)) req('BusinessEntity');
    req('StartDate');
    if (model.EmploymentStatus === 'Exited') req('EndDate');
    // Manager fields are required only when they actually sit above the
    // member in the org chart — otherwise there's no manager to name.
    //   - Director / Regional Manager: neither field shows or is required.
    //   - Liaison Manager: Regional Manager is required (they report up).
    //   - Liaison: both Regional Manager AND at least one Liaison Manager
    //     are required.
    // The HIDE_* sets drive both visibility and this required check so
    // the two stay in lockstep.
    if (!HIDE_RM_WHEN.has(model.PortalRole)) req('RegionalManager');
    if (!HIDE_LM_WHEN.has(model.PortalRole)) {
      const lms = (model.liaisonManagers || []).filter((s) => s && s.trim());
      if (lms.length === 0) next.LiaisonManagers = 'Add at least one liaison manager';
    }
    // Plant assignments are required for everyone except Directors and
    // Executives — field members (Liaisons, LMs, RMs) always own at least
    // one plant, but a Director or Executive sits above the plant-level
    // work and can exist on the roster without any plant selected.
    // Members On Leave don't need a plant either (per Jillian, Oct 2026).
    if (
      model.PortalRole !== 'Director' &&
      !HIDE_PLANTS_WHEN.has(model.PortalRole) &&
      model.EmploymentStatus !== 'On Leave'
    ) {
      if (!model.plantCodes || model.plantCodes.length === 0) {
        next.Plants = 'Select at least one plant';
      }
    }
    req('MailingStreet');
    req('MailingCity');
    // State / Province is required only for the United States and Canada;
    // optional for every other country.
    if (countryRequiresState(model.MailingCountry)) req('MailingState');
    req('MailingZip');
    req('MailingCountry');
    req('PersonalEmail');
    req('PortalRole');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const rmField = (
          <div className="form-grid">
            <div className="field full">
              <label>
                {directorAllowed ? 'Regional Manager / Director' : 'Regional Manager'}
                {model.PortalRole === 'Executive' ? '' : ' *'}
              </label>
              <select
                value={model.RegionalManager || ''}
                onChange={(e) => set('RegionalManager', e.target.value)}
              >
                <option value="">— select —</option>
                {/* Preserve a legacy free-text value so editing an older
                    record doesn't silently wipe it just because the named
                    person is missing / retitled on the roster. */}
                {model.RegionalManager &&
                  !rmOptions.includes(model.RegionalManager) && (
                    <option value={model.RegionalManager}>
                      {model.RegionalManager} (not on roster)
                    </option>
                  )}
                {rmOptions.map((name) => (
                  <option key={name} value={name}>
                    {directorNames.has(name) ? `${name} (Director)` : name}
                  </option>
                ))}
              </select>
              {rmOptions.length === 0 && (
                <span className="muted" style={{ fontSize: 13 }}>
                  No active Regional Managers on the team. Add one from the
                  Proxemics Team page (with Portal Role = Regional Manager).
                </span>
              )}
              {errors.RegionalManager && (
                <span className="error">{errors.RegionalManager}</span>
              )}
            </div>
          </div>
  );

  const submit = (e) => {
    e?.preventDefault?.();
    if (!validate()) {
      // Jump to the first invalid field so the director doesn't have
      // to scroll back up the form looking for what's missing.
      scrollToFirstError();
      return;
    }
    // Mirror the first selected plant's display name to PrimaryPlant so the
    // index's "Plant" column can render without a second lookup, and coerce
    // currency fields into numbers.
    const firstPlant = (model.plantCodes || [])
      .map((code) => plants.find((p) => p.PlantCode === code))
      .find(Boolean);
    // Reconstruct the combined mailing address string so any older reader
    // (scripts, exports, existing views) still sees a human-readable version.
    // The five structured columns remain the source of truth for the UI.
    const cityLine = [model.MailingCity, model.MailingState, model.MailingZip]
      .map((s) => (s || '').trim())
      .filter(Boolean)
      .join(', ');
    const combinedAddress = [
      (model.MailingStreet || '').trim(),
      cityLine,
      (model.MailingCountry || '').trim(),
    ]
      .filter(Boolean)
      .join('\n');
    // SharePoint's Graph API rejects "" for Date and Number columns — it
    // wants a real value or `null`. Normalize here so a blank Start/End/
    // Birth date or Pay rate doesn't blow up the save.
    const nullIfBlank = (v) => (v === '' || v == null ? null : v);
    // Executives aren't tied to a plant or a manager. Even if the record
    // previously had plant codes / managers (e.g. someone promoted from a
    // field role), clear them on save so the person never shows up on a
    // plant's derived team roster or org tree.
    const isExecutive = HIDE_PLANTS_WHEN.has(model.PortalRole);
    // Supplier Assignment applies to Liaisons AND Liaison Managers (Sep
    // 2026 — for LMs it decides which suppliers they bill at a plant shared
    // with another LM; see utils/supplierScope.js). Cleared for every other
    // role, and pruned to plants still assigned so a dropped plant doesn't
    // leave orphaned supplier picks behind.
    const prunedSupplierAssignments = {};
    if (SUPPLIER_ASSIGNMENT_ROLES.has(model.PortalRole)) {
      for (const code of model.plantCodes || []) {
        const list = Array.isArray(model.supplierAssignments?.[code])
          ? model.supplierAssignments[code].filter(Boolean)
          : [];
        if (list.length) prunedSupplierAssignments[code] = list;
      }
    }
    const payload = {
      ...model,
      plantCodes: isExecutive ? [] : model.plantCodes,
      // Executives keep their (optional) manager since Oct 2026.
      RegionalManager: model.RegionalManager,
      liaisonManagers: isExecutive ? [] : model.liaisonManagers,
      supplierAssignments: prunedSupplierAssignments,
      PrimaryPlant: isExecutive ? '' : firstPlant?.Title || '',
      StartDate: nullIfBlank(model.StartDate),
      EndDate: nullIfBlank(model.EndDate),
      Birthdate: nullIfBlank(model.Birthdate),
      PayRate: model.PayRate === '' || model.PayRate == null ? null : Number(model.PayRate),
      MailingAddress: combinedAddress,
    };
    onSave?.(payload);
  };

  // Error summary banner was removed — every form now relies on the
  // global red-box-around-field treatment so the missing fields jump
  // out at a glance without a separate callout at the top of the
  // form. Keeping the layout as a plain <form> for consistency with
  // PlantForm / SupplierForm / PartForm.
  return (
    <form onSubmit={submit} className="team-form" noValidate>
      <Section title="Basic Info">
        <div className="form-grid">
          <TextField
            label="First Name *"
            value={model.FirstName}
            onChange={(v) => set('FirstName', v)}
            error={errors.FirstName}
          />
          <TextField
            label="Last Name *"
            value={model.LastName}
            onChange={(v) => set('LastName', v)}
            error={errors.LastName}
          />
          <TextField
            label="Nickname"
            value={model.Nickname}
            onChange={(v) => set('Nickname', v)}
          />
          <TextField
            label="Job Title / Role *"
            value={model.Role}
            onChange={(v) => set('Role', v)}
            error={errors.Role}
            full
          />
          {/* Proxemics email — domain is locked, director only types the
              local part. Pasting a full address strips out @... so the
              suffix always reflects the stored value. */}
          <div className="field full">
            <label>Proxemics Email *</label>
            <div style={{ display: 'flex', alignItems: 'stretch' }}>
              <input
                type="text"
                value={emailLocalPart(model.Email)}
                onChange={(e) => {
                  const clean = (e.target.value || '').split('@')[0];
                  set('Email', clean ? `${clean}${PROXEMICS_EMAIL_DOMAIN}` : '');
                }}
                placeholder="firstname.lastname"
                style={{
                  flex: 1,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              />
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 10px',
                  border: '1px solid var(--brand-border)',
                  borderLeft: 0,
                  borderTopRightRadius: 'var(--radius-sm)',
                  borderBottomRightRadius: 'var(--radius-sm)',
                  background: '#f8fafc',
                  color: 'var(--brand-muted, #64748b)',
                  fontSize: 14,
                  whiteSpace: 'nowrap',
                }}
              >
                {PROXEMICS_EMAIL_DOMAIN}
              </span>
            </div>
            {errors.Email && <span className="error">{errors.Email}</span>}
          </div>
          <PhoneField
            label="Phone *"
            value={model.Phone}
            onChange={(v) => set('Phone', v)}
            error={errors.Phone}
          />
          {/* QR sits inside the same form-grid as Phone so its label styling
              matches the TextFields above it — no bespoke heading/paragraph. */}
          <div className="field full">
            <label>QR Code Image</label>
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={(e) => onQrFile(e.target.files?.[0])}
            />
            {(qrError || errors.QRImage) && (
              <span className="error">{qrError || errors.QRImage}</span>
            )}
            {model.QRImage && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                <img
                  src={model.QRImage}
                  alt="QR preview"
                  style={{
                    width: 120,
                    height: 120,
                    objectFit: 'contain',
                    background: '#fff',
                    border: '1px solid var(--brand-border, #e5e7eb)',
                    borderRadius: 6,
                  }}
                />
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => set('QRImage', '')}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* Role Designation & Duties — pulls together the "what is this person
          to us" fields: Designation (employee / contractor / etc.), the
          portal role they'll sign in as, and two duty flags the billing /
          reporting flows key off of. Sits before Employment so the director
          picks the portal-facing role + duties up front, then fills in the
          hiring basics underneath. */}
      <Section title="Role Designation & Duties">
        <div className="form-grid">
          <SelectField
            label="Designation *"
            value={model.EmploymentType}
            onChange={(v) => set('EmploymentType', v)}
            options={EMPLOYMENT_TYPES}
          />
          {/* Business Entity only applies to Contractor / Sub-Contractor —
              pops in directly under Designation as a full-width row so the
              director sees it the instant the designation changes. */}
          {ENTITY_REQUIRED_TYPES.has(model.EmploymentType) && (
            <TextField
              label="Business Entity *"
              value={model.BusinessEntity}
              onChange={(v) => set('BusinessEntity', v)}
              placeholder="e.g. Smith Consulting LLC"
              error={errors.BusinessEntity}
              full
            />
          )}
          <SelectField
            label="Portal Role *"
            value={model.PortalRole}
            onChange={(v) => set('PortalRole', v)}
            // 'None' is hidden from the dropdown so every new team member
            // gets an explicit role. It's still a valid stored value for
            // legacy rows; those render as "— select —" until updated.
            options={PORTAL_ROLES.filter((r) => r !== 'None').map((r) => ({
              value: r,
              label: formatRole(r),
            }))}
          />
          {/* Manually-tracked portal access. Optional — most employees
              are on email-only Microsoft plans and stay at No Access;
              set Pending Setup while the license / Entra assignment is
              in flight, then Active once they can sign in. */}
          <SelectField
            label="Portal Access"
            value={model.PortalAccessStatus}
            onChange={(v) => set('PortalAccessStatus', v)}
            options={[
              { value: 'Active', label: 'Active' },
              { value: 'Pending Setup', label: 'Pending Setup' },
              { value: 'No Access', label: 'No Access' },
            ]}
          />
          {/* Two duty flags. These are separate from Designation because
              whether someone sends weekly reports or submits timesheets
              doesn't line up cleanly with their employment type — an
              Employee might do both, a Contractor might do one, a Visitor
              neither. Rendered as a full-width row of checkboxes so they
              stay visually grouped. */}
          <div className="field full">
            <label>Duties</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 4 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <input
                  type="checkbox"
                  checked={!!model.SendsReports}
                  onChange={(e) => set('SendsReports', e.target.checked)}
                />
                Sends Reports
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <input
                  type="checkbox"
                  checked={!!model.SubmitsTimesheets}
                  onChange={(e) => set('SubmitsTimesheets', e.target.checked)}
                />
                Submits Billing Tracker
              </label>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Employment">
        <div className="form-grid">
          <SelectField
            label="Employment Status *"
            value={model.EmploymentStatus}
            onChange={(v) => {
              // Switching someone to Exited fills End Date with today's
              // date (per Jillian, Oct 2026); it stays editable below.
              if (v === 'Exited' && model.EmploymentStatus !== 'Exited') {
                const t = new Date();
                const today = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
                setModel((m) => ({ ...m, EmploymentStatus: v, EndDate: today }));
                return;
              }
              set('EmploymentStatus', v);
            }}
            options={EMPLOYMENT_STATUSES}
          />
          <TextField
            label="Start Date *"
            type="date"
            value={model.StartDate}
            onChange={(v) => set('StartDate', v)}
            error={errors.StartDate}
          />
          {/* End date only surfaces when someone has actually exited — no
              reason to prompt for it on active or on-leave members. */}
          {model.EmploymentStatus === 'Exited' && (
            <TextField
              label="End Date *"
              type="date"
              value={model.EndDate}
              onChange={(v) => set('EndDate', v)}
              error={errors.EndDate}
            />
          )}
          <TextField
            label="Pay Rate (USD/hr)"
            type="number"
            step="0.01"
            value={model.PayRate}
            onChange={(v) => set('PayRate', v)}
          />
        </div>
      </Section>

      {/* Executives (staff) have no Plant Assignment section, so their
          optional manager gets its own small section (Oct 2026). */}
      {model.PortalRole === 'Executive' && (
        <Section title="Reports To">{rmField}</Section>
      )}
      {/* Portal Role is captured in Role Designation & Duties above — it
          drives whether Regional / Liaison Manager fields appear in the
          Plant Assignment block below. Executives aren't assigned to a
          plant and have no manager, so the whole section is hidden for them. */}
      {!HIDE_PLANTS_WHEN.has(model.PortalRole) && (
      <Section title="Plant Assignment">
        {!HIDE_RM_WHEN.has(model.PortalRole) && rmField}
        {!HIDE_LM_WHEN.has(model.PortalRole) && (
          <div className="field full">
            <label>Liaison Manager(s) *</label>
            <div
              style={{
                border: errors.LiaisonManagers
                  ? '1.5px solid var(--brand-danger)'
                  : '1px solid var(--brand-border, #e5e7eb)',
                borderRadius: 6,
                padding: 8,
                maxHeight: 200,
                overflowY: 'auto',
                background: errors.LiaisonManagers ? '#fef2f2' : undefined,
              }}
            >
              {lmOptions.length === 0 &&
                (model.liaisonManagers || []).length === 0 && (
                  <div className="muted" style={{ padding: 8, fontSize: 13 }}>
                    No active Liaison Managers on the team. Add one from the
                    Proxemics Team page (with Portal Role = Liaison Manager).
                  </div>
                )}
              {/* Legacy stored names that aren't on the current roster still
                  render so they can be reviewed / unchecked rather than
                  silently vanishing when an LM leaves the team. */}
              {(model.liaisonManagers || [])
                .filter((n) => n && !lmOptions.includes(n))
                .map((name) => (
                  <label
                    key={`legacy-${name}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 4px',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked
                      onChange={(e) => toggleLm(name, e.target.checked)}
                    />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      {name}
                      <span
                        className="muted"
                        style={{ marginLeft: 6, fontWeight: 400 }}
                      >
                        (not on roster)
                      </span>
                    </span>
                  </label>
                ))}
              {lmOptions.map((name) => {
                const checked = (model.liaisonManagers || []).includes(name);
                return (
                  <label
                    key={name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 4px',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => toggleLm(name, e.target.checked)}
                    />
                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                      {name}
                    </span>
                  </label>
                );
              })}
            </div>
            {errors.LiaisonManagers && (
              <span className="error">{errors.LiaisonManagers}</span>
            )}
          </div>
        )}
        {/* Wrapped in a single .field so the error span is properly
            associated with the picker — without this the .error sits
            as an orphan sibling and scrollToFirstError can't find it
            on submit. */}
        <div className="field full" style={{ marginTop: 4 }}>
          {/* Plants are required unless the member is a Director — the
              asterisk tracks the same condition the validator uses so the
              UI and the rule never drift apart. */}
          <label>
            Plants
            {model.PortalRole !== 'Director' && model.EmploymentStatus !== 'On Leave'
              ? ' *'
              : ''}
          </label>
          <div
            style={{
              border: errors.Plants
                ? '1.5px solid var(--brand-danger)'
                : '1px solid var(--brand-border, #e5e7eb)',
              borderRadius: 6,
              padding: 8,
              maxHeight: 240,
              overflowY: 'auto',
              background: errors.Plants ? '#fef2f2' : undefined,
            }}
          >
            {plants.length === 0 && (
              <div className="muted" style={{ padding: 8, fontSize: 13 }}>
                No plants yet — add one from the Plants page first.
              </div>
            )}
            {plants.map((p) => {
              const checked = (model.plantCodes || []).includes(p.PlantCode);
              return (
                <label
                  key={p.id || p.PlantCode}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '6px 4px',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => togglePlant(p.PlantCode, e.target.checked)}
                  />
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    {p.Title || p.PlantCode}
                    <span className="muted" style={{ marginLeft: 6, fontSize: 13, fontWeight: 400 }}>
                      {p.PlantCode}
                      {p.OEM ? ` · ${p.OEM}` : ''}
                      {p.City ? ` · ${p.City}${p.State ? ', ' + p.State : ''}` : ''}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
          {errors.Plants && (
            <span className="error" style={{ marginTop: 6, display: 'block' }}>
              {errors.Plants}
            </span>
          )}
        </div>
        {/* Supplier Assignment — Liaisons only (per Jillian, July 2026).
            One checklist per assigned plant, listing the suppliers with
            coverage at that plant, so a Director can record exactly
            which supplier(s) the liaison supports — often more than
            one. Stored as { plantCode: [supplierCodes] }. */}
        {SUPPLIER_ASSIGNMENT_ROLES.has(model.PortalRole) &&
          (model.plantCodes || []).length > 0 && (
            <div className="field full" style={{ marginTop: 4 }}>
              <label>Supplier Assignment</label>
              <p
                className="muted"
                style={{ margin: '2px 0 6px', fontSize: 13 }}
              >
                {model.PortalRole === 'LiaisonManager'
                  ? 'Which supplier/s does this Liaison Manager bill at each plant? This only matters at a plant with more than one Liaison Manager. Where they are the only manager, every supplier at the plant (current and future) is theirs automatically.'
                  : 'Which supplier/s does this liaison support at each plant? Check all that apply.'}
              </p>
              {(model.plantCodes || []).map((code) => {
                const plant = plants.find((p) => p.PlantCode === code);
                const opts = suppliersForPlant(code);
                const chosen = Array.isArray(model.supplierAssignments?.[code])
                  ? model.supplierAssignments[code]
                  : [];
                // For Liaison Managers: who else manages this plant? Other
                // LMs on the roster, excluding this record. Drives the
                // per-plant note below.
                const otherLms =
                  model.PortalRole === 'LiaisonManager'
                    ? liaisonManagersAtPlant(team, code).filter(
                        (m) => String(m.id || '') !== String(model.id || initial?.id || ''),
                      )
                    : [];
                const chosenUpper = chosen.map((c) =>
                  String(c || '').toUpperCase().trim(),
                );
                // Saved codes whose supplier no longer covers this plant
                // (or was deleted) still render as checkable entries so
                // they can be seen and unchecked rather than silently
                // lingering in the record.
                const stale = chosenUpper.filter(
                  (c) =>
                    !opts.some(
                      (s) =>
                        String(s.SupplierCode || '').toUpperCase().trim() === c,
                    ),
                );
                return (
                  <div
                    key={code}
                    style={{
                      border: '1px solid var(--brand-border, #e5e7eb)',
                      borderRadius: 6,
                      padding: 8,
                      marginTop: 8,
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                      {plant?.Title || code}
                      <span
                        className="muted"
                        style={{ marginLeft: 6, fontWeight: 400 }}
                      >
                        {code}
                      </span>
                    </div>
                    {/* Liaison Manager notes: sole manager → checklist is
                        informational; shared plant with nothing checked →
                        their tracker will be empty here until fixed. */}
                    {model.PortalRole === 'LiaisonManager' &&
                      otherLms.length === 0 && (
                        <div className="muted" style={{ padding: '2px 4px 6px', fontSize: 12 }}>
                          Only Liaison Manager at this plant: all suppliers
                          here are theirs automatically. Checking boxes is
                          optional.
                        </div>
                      )}
                    {model.PortalRole === 'LiaisonManager' &&
                      otherLms.length > 0 && (
                        <div
                          style={{
                            padding: '2px 4px 6px',
                            fontSize: 12,
                            color:
                              chosen.filter(Boolean).length === 0
                                ? 'var(--brand-danger)'
                                : 'var(--brand-muted)',
                          }}
                        >
                          Shared with{' '}
                          {otherLms
                            .map((m) => m.Title || `${m.FirstName || ''} ${m.LastName || ''}`.trim())
                            .join(', ')}
                          .{' '}
                          {chosen.filter(Boolean).length === 0
                            ? 'Nothing checked: this manager\'s Billing Tracker will show no suppliers at this plant until you check the ones they bill.'
                            : 'Only the checked suppliers appear on their Billing Tracker here.'}
                        </div>
                      )}
                    {opts.length === 0 && stale.length === 0 && (
                      <div className="muted" style={{ padding: 4, fontSize: 13 }}>
                        No suppliers at this plant yet — add coverage on the
                        Suppliers page first.
                      </div>
                    )}
                    {opts.map((s) => {
                      const sc = String(s.SupplierCode || '').toUpperCase().trim();
                      const checked = chosenUpper.includes(sc);
                      return (
                        <label
                          key={sc}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '4px 4px',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) =>
                              toggleSupplierAssignment(code, sc, e.target.checked)
                            }
                          />
                          <span style={{ fontSize: 13, fontWeight: 500 }}>
                            {s.Title || sc}
                            <span
                              className="muted"
                              style={{ marginLeft: 6, fontSize: 13, fontWeight: 400 }}
                            >
                              {sc}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                    {stale.map((sc) => (
                      <label
                        key={sc}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '4px 4px',
                          cursor: 'pointer',
                        }}
                        title="This supplier no longer has coverage at this plant"
                      >
                        <input
                          type="checkbox"
                          checked
                          onChange={(e) =>
                            toggleSupplierAssignment(code, sc, e.target.checked)
                          }
                        />
                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                          {sc}
                          <span
                            className="muted"
                            style={{ marginLeft: 6, fontWeight: 400 }}
                          >
                            no longer at this plant
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
      </Section>
      )}

      <Section title="Personal">
        {/* Mailing address is broken into five columns so the data is
            structured — easier to sort, validate, and eventually map. The old
            free-text `MailingAddress` column is still mirrored on save for any
            legacy readers. */}
        <div className="form-grid">
          <TextField
            label="Mailing Address *"
            value={model.MailingStreet}
            onChange={(v) => set('MailingStreet', v)}
            placeholder="Street address"
            error={errors.MailingStreet}
            full
          />
          <TextField
            label="Address Line 2"
            value={model.MailingStreet2}
            onChange={(v) => set('MailingStreet2', v)}
            full
          />
          <TextField
            label="Address Line 3"
            value={model.MailingStreet3}
            onChange={(v) => set('MailingStreet3', v)}
            full
          />
          <SelectField
            label="Country *"
            value={model.MailingCountry}
            onChange={(v) => set('MailingCountry', v)}
            options={countryOptions}
            placeholder="— select country —"
            error={errors.MailingCountry}
          />
          {countryHasStateList(model.MailingCountry) ? (
            <SelectField
              label={`State / Province${countryRequiresState(model.MailingCountry) ? ' *' : ''}`}
              value={model.MailingState}
              onChange={(v) => set('MailingState', v)}
              options={stateOptions}
              placeholder={
                model.MailingCountry ? '— select state/province —' : '— select country first —'
              }
              error={errors.MailingState}
            />
          ) : (
            <TextField
              label={`State / Province${countryRequiresState(model.MailingCountry) ? ' *' : ''}`}
              value={model.MailingState}
              onChange={(v) => set('MailingState', v)}
              error={errors.MailingState}
              disabled={!model.MailingCountry}
              placeholder={
                model.MailingCountry
                  ? 'Enter state / province / region'
                  : '— select country first —'
              }
            />
          )}
          <TextField
            label="City *"
            value={model.MailingCity}
            onChange={(v) => set('MailingCity', v)}
            error={errors.MailingCity}
          />
          <TextField
            label="Zip / Postal Code *"
            value={model.MailingZip}
            onChange={(v) => set('MailingZip', v)}
            error={errors.MailingZip}
          />
          <TextField
            label="Personal Email *"
            type="email"
            value={model.PersonalEmail}
            onChange={(v) => set('PersonalEmail', v)}
            error={errors.PersonalEmail}
          />
          {/* The native <input type="date"> fought us for birth years: any
              `min` we set made Chrome reject intermediate year digits (typing
              "1" on its way to "1985" resolves to year 0001, below min, so
              the browser discards the keystroke and the year never gets
              entered). Three independent dropdowns sidestep the picker
              entirely — director picks month/day/year, and we compose the
              ISO string SharePoint expects underneath. */}
          <BirthdateField
            value={model.Birthdate}
            onChange={(v) => set('Birthdate', v)}
          />
          {/* Preferred shirt fit + size — defaults a Shirt / Vest request
              so the member doesn't have to re-pick them every time, and
              both also show up in the Shirt Size tile on My Account where
              the member can update them themselves. Fit is listed first
              because it usually drives which size chart applies. */}
          <SelectField
            label="Shirt Fit"
            value={model.ShirtFit}
            onChange={(v) => set('ShirtFit', v)}
            options={SHIRT_FITS}
          />
          <SelectField
            label="Shirt Size"
            value={model.ShirtSize}
            onChange={(v) => set('ShirtSize', v)}
            options={APPAREL_SIZES}
          />
          {/* Safety Vest size + color. Same size chart as shirts. Color
              picks from the same VEST_COLORS list the Vest request form
              uses — "Other" reveals a free-text fill-in so uncommon
              colors (e.g. Hi-Vis Green) can be captured. */}
          <SelectField
            label="Safety Vest Size"
            value={model.VestSize}
            onChange={(v) => set('VestSize', v)}
            options={APPAREL_SIZES}
          />
          <SelectField
            label="Safety Vest Color"
            value={model.VestColor}
            onChange={(v) => {
              set('VestColor', v);
              // Clear the "Other" fill-in when the user switches off Other,
              // so stale free-text doesn't hang around on the record.
              if (v !== 'Other') set('VestColorOther', '');
            }}
            options={VEST_COLORS}
          />
          {model.VestColor === 'Other' && (
            <TextField
              label="Other Color"
              value={model.VestColorOther}
              onChange={(v) => set('VestColorOther', v)}
              placeholder="e.g. Hi-Vis Green"
            />
          )}
        </div>
      </Section>

      <Section title="Emergency Contact">
        <div className="form-grid">
          <TextField
            label="Name"
            value={model.EmergencyContactName}
            onChange={(v) => set('EmergencyContactName', v)}
          />
          <PhoneField
            label="Phone"
            value={model.EmergencyContactPhone}
            onChange={(v) => set('EmergencyContactPhone', v)}
          />
          <RelationField
            value={model.EmergencyContactRelation}
            onChange={(v) => set('EmergencyContactRelation', v)}
          />
        </div>
      </Section>

      <Section title="Notes">
        <div className="form-grid">
          {/* Section title already reads "Notes" — drop the inner label so
              it doesn't repeat. */}
          <TextArea
            label=""
            value={model.Notes}
            onChange={(v) => set('Notes', v)}
            rows={3}
          />
        </div>
      </Section>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginTop: 16,
        }}
      >
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          * Required fields
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-outline"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : initial?.id ? 'Save Changes' : 'Add Team Member'}
          </button>
        </div>
      </div>
    </form>
  );
}

// ---- helpers --------------------------------------------------------------

// Month / day / year dropdown trio used for Birthdate. Splitting the date
// input into three independent selects means the director doesn't have to
// fight the native picker's "spinner pinned to today" behaviour — picking
// a 1962 birth year is the same as picking 2024. We compose the ISO
// YYYY-MM-DD string on change; downstream (validate / save) keeps working
// against model.Birthdate the same way as before.
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
function BirthdateField({ value, onChange }) {
  // Each dropdown needs to remember what the user picked even before the
  // other two are filled in — otherwise picking "March" alone would emit
  // an empty Birthdate (we can't form a valid ISO date without day + year)
  // and the select would immediately snap back to "Month". Local state
  // decouples the visible selection from the composed model value; we
  // only call onChange once the trio produces a savable date (or when
  // the user clears one back out).
  const initial = parseBirthdate(value);
  const [month, setMonth] = useState(initial.month);
  const [day, setDay] = useState(initial.day);
  const [year, setYear] = useState(initial.year);

  // If the parent resets the form (edit → new, or loading a different
  // member), re-sync the local selections to whatever the model now says.
  useEffect(() => {
    const next = parseBirthdate(value);
    setMonth(next.month);
    setDay(next.day);
    setYear(next.year);
  }, [value]);

  const currentYear = new Date().getFullYear();
  // Years descend (most-recent first) so the common case — a working-age
  // adult — is only a short scroll. Goes back to 1925 which covers anyone
  // realistically on the roster; easy to widen later.
  const yearOptions = [];
  for (let yr = currentYear; yr >= 1925; yr -= 1) yearOptions.push(String(yr));
  const dayOptions = [];
  for (let dy = 1; dy <= 31; dy += 1) {
    dayOptions.push(String(dy).padStart(2, '0'));
  }

  const emit = (nextYear, nextMonth, nextDay) => {
    if (!nextYear || !nextMonth || !nextDay) {
      onChange?.('');
      return;
    }
    onChange?.(`${nextYear}-${nextMonth}-${nextDay}`);
  };

  return (
    <div className="field">
      <label>Birthdate</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <select
          value={month}
          onChange={(e) => {
            setMonth(e.target.value);
            emit(year, e.target.value, day);
          }}
          style={{ flex: 2 }}
          aria-label="Birth month"
        >
          <option value="">Month</option>
          {MONTH_NAMES.map((name, i) => {
            const val = String(i + 1).padStart(2, '0');
            return (
              <option key={val} value={val}>
                {name}
              </option>
            );
          })}
        </select>
        <select
          value={day}
          onChange={(e) => {
            setDay(e.target.value);
            emit(year, month, e.target.value);
          }}
          style={{ flex: 1 }}
          aria-label="Birth day"
        >
          <option value="">Day</option>
          {dayOptions.map((val) => (
            <option key={val} value={val}>
              {Number(val)}
            </option>
          ))}
        </select>
        <select
          value={year}
          onChange={(e) => {
            setYear(e.target.value);
            emit(e.target.value, month, day);
          }}
          style={{ flex: 1 }}
          aria-label="Birth year"
        >
          <option value="">Year</option>
          {yearOptions.map((val) => (
            <option key={val} value={val}>
              {val}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

// Pull month / day / year parts out of a stored "YYYY-MM-DD" string.
// Any blank / malformed input falls through to empty strings so the
// three dropdowns show their placeholder options on an empty form.
function parseBirthdate(value) {
  const [y, mo, d] = String(value || '').split('-');
  return { year: y || '', month: mo || '', day: d || '' };
}

function buildModel(initial) {
  const safe = initial || {};
  // `plantCodes` comes from the service layer; fall back to a one-element
  // array if the record is an older single-plant row.
  const plantCodes =
    Array.isArray(safe.plantCodes) && safe.plantCodes.length
      ? safe.plantCodes
      : safe.PlantCode
        ? [safe.PlantCode]
        : [];
  const liaisonManagers = Array.isArray(safe.liaisonManagers)
    ? safe.liaisonManagers
    : [];
  return {
    FirstName: safe.FirstName || '',
    LastName: safe.LastName || '',
    Nickname: safe.Nickname || '',
    Title: safe.Title || '',
    Role: safe.Role || '',
    EmploymentType: safe.EmploymentType || 'Employee',
    EmploymentStatus: safe.EmploymentStatus || 'Active',
    Email: safe.Email || '',
    Phone: safe.Phone || '',
    QRImage: safe.QRImage || '',
    QRLink: safe.QRLink || '',
    BusinessEntity: safe.BusinessEntity || '',
    PayRate: safe.PayRate ?? '',
    StartDate: safe.StartDate ? String(safe.StartDate).slice(0, 10) : '',
    EndDate: safe.EndDate ? String(safe.EndDate).slice(0, 10) : '',
    plantCodes,
    RegionalManager: safe.RegionalManager || '',
    liaisonManagers,
    // Liaison-only per-plant supplier support: { PLANTCODE: [codes] }.
    supplierAssignments:
      safe.supplierAssignments && typeof safe.supplierAssignments === 'object'
        ? safe.supplierAssignments
        : {},
    // New structured address columns. If the record was created before the
    // split, stash the old blob into MailingStreet as a best-effort seed so
    // the director can retype it into the correct fields on next save.
    MailingStreet:
      safe.MailingStreet || (safe.MailingStreet === '' ? '' : safe.MailingAddress || ''),
    MailingStreet2: safe.MailingStreet2 || '',
    MailingStreet3: safe.MailingStreet3 || '',
    MailingCity: safe.MailingCity || '',
    MailingState: safe.MailingState || '',
    MailingZip: safe.MailingZip || '',
    MailingCountry: safe.MailingCountry || '',
    // Kept so legacy readers still see *something* — we rewrite it on save.
    MailingAddress: safe.MailingAddress || '',
    PersonalEmail: safe.PersonalEmail || '',
    Birthdate: safe.Birthdate ? String(safe.Birthdate).slice(0, 10) : '',
    EmergencyContactName: safe.EmergencyContactName || '',
    EmergencyContactPhone: safe.EmergencyContactPhone || '',
    EmergencyContactRelation: safe.EmergencyContactRelation || '',
    ShirtSize: safe.ShirtSize || '',
    ShirtFit: safe.ShirtFit || '',
    // Safety Vest size + color. VestColor is the preset pick (Yellow /
    // Orange / Black / Other); when "Other" is picked, VestColorOther
    // holds the fill-in text so we don't lose the canonical preset.
    VestSize: safe.VestSize || '',
    VestColor: safe.VestColor || '',
    VestColorOther: safe.VestColorOther || '',
    // Start blank so the dropdown shows "— select —" by default and the
    // director has to make an explicit choice. Legacy rows stored as 'None'
    // also fall through to blank so they get re-picked on next save.
    PortalRole: safe.PortalRole && safe.PortalRole !== 'None' ? safe.PortalRole : '',
    // Manually-tracked portal access status — Active / Pending Setup /
    // No Access. Records whether the person's M365 license + Entra role
    // assignment are actually in place (most employees are on email-only
    // plans and never get portal access).
    PortalAccessStatus: safe.PortalAccessStatus || '',
    // Duty flags. Coerced with !! so a missing SharePoint column reads as
    // false instead of undefined (which would log-warn on the checkbox).
    SendsReports: !!safe.SendsReports,
    SubmitsTimesheets: !!safe.SubmitsTimesheets,
    Notes: safe.Notes || '',
    id: safe.id,
  };
}

// Display name for a team record. Prefers Title when present (already synced
// to "First Last" by the service layer) so we stay consistent with how the
// org chart and Plant detail render names.
function fullName(m) {
  if (!m) return '';
  if (m.Title && String(m.Title).trim()) return String(m.Title).trim();
  const first = (m.FirstName || '').trim();
  const last = (m.LastName || '').trim();
  return [first, last].filter(Boolean).join(' ').trim();
}

function formatRole(r) {
  if (r === 'RegionalManager') return 'Regional Manager';
  if (r === 'LiaisonManager') return 'Liaison Manager';
  return r;
}

// Extract just the "firstname.lastname" part for the visible input. Tolerant
// of legacy values with a non-Proxemics domain and of paste-in-the-whole-
// address users; we always drop everything from the @ onward.
function emailLocalPart(email) {
  if (!email) return '';
  const at = email.indexOf('@');
  return at >= 0 ? email.slice(0, at) : email;
}

/**
 * Load an image file, draw it onto a square canvas at `maxDim` × `maxDim`
 * (preserving aspect ratio, letterboxed on white so the QR stays readable),
 * and return a PNG data URL. Keeps the payload under SharePoint's Note
 * column limit without a server round-trip.
 */
function fileToResizedDataUrl(file, maxDim = 400) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error || new Error('read failed'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('image decode failed'));
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = maxDim;
        canvas.height = maxDim;
        const ctx = canvas.getContext('2d');
        // White background so a transparent QR still shows as black-on-white.
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, maxDim, maxDim);
        const scale = Math.min(maxDim / img.width, maxDim / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (maxDim - w) / 2, (maxDim - h) / 2, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
